import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken, useTenantData } from "@/hooks";
import { BalanceApi } from "@/openapi/generated";
import type { PostBalanceTransactionRequest } from "@/openapi/generated";
import { createAuthenticatedApi } from "@/lib/utils";
import { toRequestCurrency } from "@/lib/utils/currency";
import { useToast } from "@/components/feedback";
import { getApiErrorMessage } from "@/lib/utils/api/error-message";
import { invalidateBalanceQueries } from "@/features/balance/utils/invalidate-balance-queries";

export const useBalanceAdjust = (toast: ReturnType<typeof useToast>) => {
  const accessToken = useAccessToken();
  const queryClient = useQueryClient();
  const { tenantId, tenantData } = useTenantData();

  return useMutation({
    mutationFn: async (
      postBalanceTransactionRequest: PostBalanceTransactionRequest,
    ) => {
      if (!accessToken) throw new Error("Authentication token is required");
      const service = createAuthenticatedApi(BalanceApi, accessToken);
      await service.apiBalancePostTransactionPost({
        postBalanceTransactionRequest: {
          ...postBalanceTransactionRequest,
          currency: toRequestCurrency(
            postBalanceTransactionRequest.currency || tenantData?.currency,
          ),
        },
        xTenantID: tenantId,
      });
    },
    onSuccess: async () => {
      await invalidateBalanceQueries(queryClient);
      toast({
        title: "Balance adjusted",
        description: "Balance line has been added successfully.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      });
    },
    onError: async (error) => {
      const message = await getApiErrorMessage(error, "Failed to adjust balance");
      toast({
        title: "Error",
        description: message,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    },
  });
};
